import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getNextStrengthWorkout } from '../utils/database';
import HomeIcon from '../assets/icons/home.svg';
import CalendarIcon from '../assets/icons/calendar.svg';
import ActivityIcon from '../assets/icons/activity.svg';
import ChartIcon from '../assets/icons/chart.svg';
import MoreIcon from '../assets/icons/more.svg';
import MorePanel from './MorePanel';
import '../styles/BottomNav.css';

function BottomNav({ userId, user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMorePanelOpen, setIsMorePanelOpen] = useState(false);
  const [nextWorkout, setNextWorkout] = useState(null);

  // Load next strength workout on mount and when userId changes
  useEffect(() => {
    if (userId) {
      const loadNextWorkout = async () => {
        try {
          const workoutData = await getNextStrengthWorkout(userId);
          setNextWorkout(workoutData);
        } catch (error) {
          console.error('[BottomNav] Error loading next workout:', error);
        }
      };
      loadNextWorkout();
    }
  }, [userId]);

  const isActive = (path) => location.pathname === path;

  const handleNavigation = (path) => {
    navigate(path);
  };

  const handleStartWorkout = () => {
    if (nextWorkout) {
      const workout = nextWorkout.workout;
      const templateId = workout.workoutTemplateId || workout.templateId;
      navigate(`/${userId}/workout`, {
        state: {
          workout: {
            activityId: workout.activityId,
            workoutTemplateId: templateId,
            name: workout.name,
            description: workout.description,
            scheduledDate: nextWorkout.date,
            estimatedDuration: workout.estimatedDuration || 60,
            status: workout.status,
            programId: workout.programId,
            mesocycleId: workout.mesocycleId,
            macrocycleId: workout.macrocycleId,
          }
        }
      });
    } else {
      // No workout scheduled - just go to workout page
      navigate(`/${userId}/workout`);
    }
  };

  const navItems = [
    {
      id: 'dashboard',
      icon: HomeIcon,
      label: 'Dashboard',
      path: `/${userId}/dashboard`,
    },
    {
      id: 'calendar',
      icon: CalendarIcon,
      label: 'Calendar',
      path: `/${userId}/calendar`,
    },
    {
      id: 'workout',
      icon: ActivityIcon,
      label: 'Start Workout',
      action: handleStartWorkout,
      isPrimary: true,
    },
    {
      id: 'performance',
      icon: ChartIcon,
      label: 'Performance',
      path: `/${userId}/performance`,
    },
    {
      id: 'more',
      icon: MoreIcon,
      label: 'More',
      action: () => setIsMorePanelOpen(true),
      isMore: true,
    },
  ];

  return (
    <>
      <nav className="bottom-nav">
        <div className="bottom-nav-container">
          {navItems.map((item) => {
            const active = item.path ? isActive(item.path) : false;
            
            return (
              <button
                key={item.id}
                className={`bottom-nav-item ${active ? 'active' : ''} ${item.isPrimary ? 'primary' : ''} ${item.isMore ? 'more' : ''}`}
                onClick={() => item.action ? item.action() : handleNavigation(item.path)}
                aria-label={item.label}
              >
                {item.isPrimary ? (
                  /* Primary button - horizontal pill with icon + text */
                  <div className="bottom-nav-primary-button">
                    <img src={item.icon} alt="" />
                    <span>{item.label}</span>
                  </div>
                ) : (
                  /* Standard nav item - icon above label */
                  <>
                    <div className="bottom-nav-icon">
                      <img src={item.icon} alt="" />
                    </div>
                    <span className="bottom-nav-label">{item.label}</span>
                  </>
                )}
              </button>
            );
          })}
        </div>
      </nav>

      <MorePanel
        isOpen={isMorePanelOpen}
        onClose={() => setIsMorePanelOpen(false)}
        userId={userId}
        user={user}
      />
    </>
  );
}

export default BottomNav;
